import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import AddIcon from "@material-ui/icons/Add";
import { useHistory, useRouteMatch } from "react-router-dom";

import CategorySection from "./CategorySection";
import BreadcrumbNav from "./BreadcrumbNav";
import { CustomTheme } from "../../../../../../types/customTheme";
import WrapperNode from "./WrapperNode";

interface MenuDetailsViewProps {
  menuNodeList: any;
  selectedMenuId: string;
  selectedMenuName: string;
  onBack: () => void;
  locationId: string;
  onMenuUpdate: () => void;
  isLoadingDetails?: boolean;
  setMenuNodeList: any;
  baseUrl?: string;
  categoryNode: any;
  productVersion: any;
  tags: any;
  stockDepartmentSelectedNode: any;
  stockGroupSelectedNode: any;
  selectedGroupObj: any;
  setSelectedGroupObj: any;
  setCategoryNode: any;
  setTags: any;
  setStockDepartmentSelectedNode: any;
  setStockGroupSelectedNode: any;
  setProductVersion: any;
  // Directory Edit Modal Props
  handleOpenDirectoryEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenProductEditModal?: (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModal: any,
    isRootValue: any,
  ) => void;
  handleOpenMappingModal?: (
    event: any,
    category: any,
    item: any,
    type: string,
    isMap?: any,
  ) => void;
  handleOpenProductOrDirectoryModals?: (category: any, wrapper?: any) => void;
  setIsProductAdd?: any;
  setError?: any;
  onDirectoryNavigate?: (directory: any, category: any, menuItem: any) => void;
  onProductNavigate?: (
    product: any,
    wrapper: any,
    category: any,
    menuItem: any,
  ) => void;
  currentCategory?: any;
  currentMenuItem?: any;
  isOpenWrapperEditModal?: any;
  isRoot?: any;
  firstWrapperId?: any;
  wrapperId?: any;
  removedLinkedIdLists?: any;
  handleClickLinkedId?: any;
  handleClearStrip?: any;
  handleNavigate?: any;
  onReorderComplete?: () => void;
  isEditDisabled: boolean;
}

const HIDDEN_NAMES = ["Online Hidden", "Hidden Food", "Hidden Drinks"];

const MenuDetailsView: React.FC<MenuDetailsViewProps> = ({
  menuNodeList,
  selectedMenuId,
  selectedMenuName,
  onBack,
  locationId,
  setMenuNodeList,
  productVersion,
  tags,
  handleOpenDirectoryEditModal,
  handleOpenProductEditModal,
  handleOpenMappingModal,
  handleOpenProductOrDirectoryModals,
  setIsProductAdd,
  setError,
  onDirectoryNavigate: externalOnDirectoryNavigate,
  onProductNavigate: externalOnProductNavigate,
  currentCategory,
  currentMenuItem,
  isOpenWrapperEditModal,
  isRoot,
  firstWrapperId,
  wrapperId,
  removedLinkedIdLists,
  handleClickLinkedId,
  handleClearStrip,
  handleNavigate,
  onReorderComplete,
  isEditDisabled,
}) => {
  const theme: CustomTheme = useTheme();
  const isMobile = useMediaQuery("(max-width: 960px)");
  const history = useHistory();
  const match: any = useRouteMatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOption, setSortOption] = useState("default");
  const [availabilityAnchorEl, setAvailabilityAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([
    "available",
    "unavailable",
  ]);
  // Fix: Persist view mode in localStorage
  const [isGridView, setIsGridView] = useState(() => {
    const saved = localStorage.getItem("menuViewMode");
    return saved !== null ? saved === "grid" : true;
  });
  const [currentStack, setCurrentStack] = useState<any[]>([]);
  const [isInDirectory, setIsInDirectory] = useState(false);
  const [currentDirectoryInfo, setCurrentDirectoryInfo] = useState<any>(null);
  const [isEditingProductInline, setIsEditingProductInline] = useState(false);
  const [editingProductData, setEditingProductData] = useState<any>(null);
  const [success, setSuccess] = useState("");

  const currentMenu = menuNodeList?.[selectedMenuId];
  const categories = currentMenu?.category || {};
  const categoryList = Object.values(categories);

  // Fix: Save view mode preference
  const handleGridViewToggle = (gridView: boolean) => {
    setIsGridView(gridView);
    localStorage.setItem("menuViewMode", gridView ? "grid" : "list");
  };

  // Helper function to build URL path from directory stack
  const buildPathFromStack = (
    stack: any[],
    targetDirectoryId?: number,
  ): string => {
    let path = `${match.url}`;
    for (const item of stack) {
      if (item.type === "directory") {
        path += `/directory/${item.id}`;
        if (targetDirectoryId && item.id === targetDirectoryId) {
          break;
        }
      }
    }
    return path;
  };

  // Update URL when directory navigation changes
  const updateUrlForDirectory = (stack: any[]) => {
    const newPath = buildPathFromStack(stack);
    history.replace(newPath);
  };

  // Update URL for product edit
  const updateUrlForProductEdit = (productId: number) => {
    const path = `${match.url}/product/${productId}`;
    history.replace(path);
  };

  // Handle directory navigation with URL update
  const handleDirectoryNavigate = (
    directory: any,
    category: any,
    menuItem: any,
  ) => {
    // Save current scroll position if needed
    if (!isInDirectory) {
      // Save root scroll position
    } else if (currentDirectoryInfo) {
      // Save directory scroll position
    }

    const newStack = [...currentStack];

    if (!isInDirectory && currentStack.length === 0) {
      newStack.push({
        type: "root",
        menuId: selectedMenuId,
        menuName: selectedMenuName,
        category: category,
        data: menuNodeList?.[selectedMenuId],
      });
    }

    newStack.push({
      type: "directory",
      id: directory.id,
      name: directory.name,
      category: category,
      parent: currentDirectoryInfo,
      directoryData: directory,
    });

    setCurrentStack(newStack);
    setCurrentDirectoryInfo(directory);
    setIsInDirectory(true);

    // Update URL
    updateUrlForDirectory(newStack);

    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // Handle product navigation with URL update
  const handleProductNavigate = async (
    product: any,
    wrapper: any,
    category: any,
    menuItem: any,
  ) => {
    if (!isInDirectory && !isEditingProductInline) {
      // Save root scroll position
    } else if (isInDirectory && currentDirectoryInfo) {
      // Save directory scroll position
    }

    const newStack = [...currentStack];

    if (
      !isInDirectory &&
      !isEditingProductInline &&
      currentStack.length === 0
    ) {
      newStack.push({
        type: "root",
        menuId: selectedMenuId,
        menuName: selectedMenuName,
        category: category,
        data: menuNodeList?.[selectedMenuId],
      });
    }

    newStack.push({
      type: "product-edit",
      id: product.id,
      name: `Edit: ${product.name}`,
      category: category,
      productData: product,
      wrapperData: wrapper,
    });

    setCurrentStack(newStack);
    setIsEditingProductInline(true);
    setIsInDirectory(false);
    setEditingProductData(product);

    // Update URL for product edit
    updateUrlForProductEdit(product.id);

    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // Handle breadcrumb navigation (backward navigation) - FIXED to preserve view mode
  const handleBreadcrumbNavigate = (index: number) => {
    if (index === -1) {
      // Navigate back to root
      if (isEditingProductInline && editingProductData) {
        // Save product scroll position
      }
      if (isInDirectory && currentDirectoryInfo) {
        // Save directory scroll position
      }

      setIsEditingProductInline(false);
      setIsInDirectory(false);
      setCurrentStack([]);
      setCurrentDirectoryInfo(null);
      setEditingProductData(null);

      // Update URL back to menu root
      history.replace(match.url);

      // IMPORTANT: isGridView is preserved - do NOT reset it

      // Restore root scroll position
    } else {
      if (isEditingProductInline && editingProductData) {
        // Save product scroll position
      }
      if (isInDirectory && currentDirectoryInfo) {
        // Save directory scroll position
      }

      const newStack = currentStack.slice(0, index + 1);
      const lastItem = newStack[newStack.length - 1];

      if (lastItem?.type === "root") {
        setIsEditingProductInline(false);
        setIsInDirectory(false);
        setCurrentStack([]);
        setCurrentDirectoryInfo(null);
        setEditingProductData(null);

        // Update URL back to menu root
        history.replace(match.url);

        // IMPORTANT: isGridView is preserved - do NOT reset it
      } else if (lastItem?.type === "directory") {
        setIsEditingProductInline(false);
        setIsInDirectory(true);
        setCurrentStack(newStack);

        // Find the latest directory data
        const latestMenuData = menuNodeList?.[selectedMenuId];
        let latestDirectoryData = lastItem.directoryData;

        if (latestMenuData && lastItem.category) {
          const findLatestDirectory = (wrapper: any, targetId: number): any => {
            if (!wrapper) return null;
            for (const key in wrapper) {
              if (wrapper[key].id === targetId) {
                return wrapper[key];
              }
              if (wrapper[key].wrapper) {
                const found = findLatestDirectory(
                  wrapper[key].wrapper,
                  targetId,
                );
                if (found) return found;
              }
            }
            return null;
          };

          const categoryWrapper =
            latestMenuData.category?.[lastItem.category.id]?.wrapper;
          if (categoryWrapper) {
            const latestDir = findLatestDirectory(categoryWrapper, lastItem.id);
            if (latestDir) {
              latestDirectoryData = latestDir;
            }
          }
        }

        setCurrentDirectoryInfo(latestDirectoryData);

        // Update URL for directory
        updateUrlForDirectory(newStack);

        // IMPORTANT: isGridView is preserved - do NOT reset it
      } else if (lastItem?.type === "product-edit") {
        setIsEditingProductInline(true);
        setIsInDirectory(false);
        setCurrentStack(newStack);
        setEditingProductData(lastItem.productData);

        // Update URL for product edit
        updateUrlForProductEdit(lastItem.id);

        // IMPORTANT: isGridView is preserved - do NOT reset it
      }
    }
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setSortAnchorEl(event.currentTarget);
  const handleSortClose = (option: string) => {
    if (option) setSortOption(option);
    setSortAnchorEl(null);
  };
  const handleAvailabilityClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => setAvailabilityAnchorEl(event.currentTarget);
  const handleAvailabilityClose = () => setAvailabilityAnchorEl(null);
  const toggleAvailability = (value: string) => {
    setSelectedAvailability((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  // Filter categories by menuId AND sort by viewOrder
  const filteredCategories = categoryList
    .filter((cat: any) => {
      if (!cat) return false;
      // Only show categories for the current menu
      if (cat.menuId !== parseInt(selectedMenuId)) return false;

      const isHiddenCategory = HIDDEN_NAMES.includes(cat.name);
      if (!showHidden && isHiddenCategory) return false;

      if (searchTerm) {
        const wrapperObjects = cat.wrapper ? Object.values(cat.wrapper) : [];
        const hasMatchingItem = wrapperObjects.some((item: any) =>
          item?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (!hasMatchingItem) return false;
      }
      return true;
    })
    .sort((a: any, b: any) => (a.viewOrder || 0) - (b.viewOrder || 0));

  // Parse URL params on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParts = window.location.pathname.split("/");
    const directoryIndex = urlParts.indexOf("directory");
    const productIndex = urlParts.indexOf("product");

    if (productIndex !== -1 && urlParts[productIndex + 1]) {
      // We're in a product edit view - would need to find and navigate to product
      console.log("Product view detected");
    } else if (directoryIndex !== -1 && urlParts[directoryIndex + 1]) {
      // We're in a directory view - would need to find and navigate to directory
      console.log("Directory view detected");
    }
  }, []);

  return (
    <div>
      {/* Toolbar */}
      <Box
        style={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: "12px" }}
          flexWrap="wrap"
        >
          <Button
            onClick={onBack}
            startIcon={<Icon icon="lucide:arrow-left" width="18" />}
          >
            Back to Menus
          </Button>
          <div
            style={{
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              width: "200px",
              height: "40px",
              border: "1px solid #374151",
              backgroundColor: theme.palette.background.entity_background,
            }}
          >
            <Icon
              icon="lucide:search"
              width="18"
              color={theme.palette.custom.orange.contrastText}
            />
            <input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: "none",
                backgroundColor: "transparent",
                marginLeft: "8px",
                outline: "none",
                width: "100%",
                color: theme.palette.custom.orange.contrastText,
              }}
            />
          </div>
          <Button
            endIcon={<Icon icon="tabler:chevron-down" width="16" />}
            onClick={handleAvailabilityClick}
            style={{
              color: theme.palette.custom.orange.contrastText,
              textTransform: "none",
              borderRadius: "8px",
              height: "40px",
              border: "1px solid #374151",
              padding: "0 16px",
            }}
          >
            {selectedAvailability.length > 1
              ? "All"
              : selectedAvailability[0] === "available"
              ? "Available"
              : "Unavailable"}
          </Button>
          <Menu
            anchorEl={availabilityAnchorEl}
            open={Boolean(availabilityAnchorEl)}
            onClose={handleAvailabilityClose}
          >
            <MenuItem onClick={() => toggleAvailability("available")}>
              <Checkbox
                size="small"
                checked={selectedAvailability.includes("available")}
                color="secondary"
              />
              <Typography variant="body2">Available</Typography>
            </MenuItem>
            <MenuItem onClick={() => toggleAvailability("unavailable")}>
              <Checkbox
                size="small"
                checked={selectedAvailability.includes("unavailable")}
                color="secondary"
              />
              <Typography variant="body2">Unavailable</Typography>
            </MenuItem>
          </Menu>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "12px",
            }}
          >
            <Box
              onClick={() => setShowHidden(!showHidden)}
              style={{
                width: "34px",
                height: "18px",
                backgroundColor: showHidden ? "#8b5cf6" : "#374151",
                borderRadius: "10px",
                position: "relative",
                cursor: "pointer",
                marginRight: "8px",
              }}
            >
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  position: "absolute",
                  top: "2px",
                  left: showHidden ? "18px" : "2px",
                  transition: "0.2s",
                }}
              />
            </Box>
            <Typography
              style={{ color: theme.palette.secondary.main, fontSize: "14px" }}
            >
              Show Hidden
            </Typography>
          </div>
        </Box>
        <Box display="flex" style={{ gap: "12px" }} alignItems="center">
          <Button
            startIcon={<Icon icon="lucide:eye" width="18" />}
            style={{
              backgroundColor: "transparent",
              color: theme.palette.custom.orange.contrastText,
              border: "1px solid #374151",
              borderRadius: "8px",
              height: "40px",
              textTransform: "none",
            }}
          >
            Preview Menu
          </Button>
          <Button
            startIcon={<AddIcon />}
            style={{
              backgroundColor: theme.palette.custom.yellow.main,
              color: theme.palette.custom.orange.contrastText,
              height: "40px",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
            }}
            disabled={isEditDisabled}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Top Bar with Sort and View Toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="0 24px 16px 24px"
      >
        <Button
          startIcon={<Icon icon="lucide:list-ordered" width="18" />}
          onClick={handleSortClick}
          style={{
            color: theme.palette.custom.orange.contrastText,
            textTransform: "none",
            borderRadius: "8px",
            height: "40px",
            border: "1px solid #374151",
            padding: "0 16px",
          }}
        >
          Sort:{" "}
          {sortOption === "default" ? "Manual" : sortOption.replace("-", " ")}
        </Button>
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => handleSortClose("")}
        >
          <MenuItem onClick={() => handleSortClose("default")}>
            Default (Manual)
          </MenuItem>
          <MenuItem onClick={() => handleSortClose("name-asc")}>
            Name: A to Z
          </MenuItem>
          <MenuItem onClick={() => handleSortClose("name-desc")}>
            Name: Z to A
          </MenuItem>
        </Menu>
        <Box display="flex" style={{ gap: "8px" }}>
          <IconButton
            onClick={() => handleGridViewToggle(false)}
            style={{
              backgroundColor: !isGridView
                ? theme.palette.custom.yellow.main
                : theme.palette.background.entity_background,
              borderRadius: "8px",
              padding: "8px",
              minWidth: "40px",
              height: "40px",
              color: theme.palette.custom.orange.contrastText,
            }}
          >
            <Icon icon="mdi:format-list-bulleted" width={20} height={20} />
          </IconButton>
          <IconButton
            onClick={() => handleGridViewToggle(true)}
            style={{
              backgroundColor: isGridView
                ? theme.palette.custom.yellow.main
                : theme.palette.background.entity_background,
              borderRadius: "8px",
              padding: "8px",
              minWidth: "40px",
              height: "40px",
              color: theme.palette.custom.orange.contrastText,
            }}
          >
            <Icon icon="mdi:view-grid-outline" width={20} height={20} />
          </IconButton>
        </Box>
      </Box>

      {/* Breadcrumb Navigation */}
      <BreadcrumbNav
        currentStack={currentStack}
        selectedMenuName={selectedMenuName}
        onNavigate={handleBreadcrumbNavigate}
      />

      {/* Categories Sections - Only show when not in directory or product edit mode */}
      {!isInDirectory &&
        !isEditingProductInline &&
        filteredCategories.map((cat: any) => (
          <CategorySection
            key={cat.id}
            category={cat}
            isGridView={isGridView}
            sortOption={sortOption}
            selectedAvailability={selectedAvailability}
            theme={theme}
            isMobile={isMobile}
            locationId={locationId}
            menuNodeList={menuNodeList}
            selectedMenuId={selectedMenuId}
            setMenuNodeList={setMenuNodeList}
            productVersion={productVersion}
            tags={tags}
            setSuccess={setSuccess}
            setError={setError}
            // Pass directory edit props down to WrapperNode
            handleOpenDirectoryEditModal={handleOpenDirectoryEditModal}
            handleOpenProductEditModal={handleOpenProductEditModal}
            handleOpenMappingModal={handleOpenMappingModal}
            handleOpenProductOrDirectoryModals={
              handleOpenProductOrDirectoryModals
            }
            setIsProductAdd={setIsProductAdd}
            onDirectoryNavigate={handleDirectoryNavigate}
            onProductNavigate={handleProductNavigate}
            currentCategory={currentCategory}
            currentMenuItem={currentMenuItem}
            isOpenWrapperEditModal={isOpenWrapperEditModal}
            isRoot={isRoot}
            firstWrapperId={firstWrapperId}
            wrapperId={wrapperId}
            removedLinkedIdLists={removedLinkedIdLists}
            handleClickLinkedId={handleClickLinkedId}
            handleClearStrip={handleClearStrip}
            handleNavigate={handleNavigate}
            isInDirectory={isInDirectory}
            currentDirectoryInfo={currentDirectoryInfo}
            onReorderComplete={onReorderComplete}
            isEditDisabled={isEditDisabled}
          />
        ))}

      {/* Directory Content View - Show when in directory mode */}
      {isInDirectory && currentDirectoryInfo && (
        <div style={{ padding: "0 24px 24px 24px" }}>
          <Grid container spacing={isGridView ? 2 : 0}>
            {currentDirectoryInfo.wrapper &&
              Object.values(currentDirectoryInfo.wrapper).map(
                (wrapperItem: any, idx: any) => (
                  <WrapperNode
                    key={`${wrapperItem?.id}_${idx}`}
                    wrapper={wrapperItem}
                    category={currentDirectoryInfo}
                    isGridView={isGridView}
                    selectedIds={[]}
                    handleItemSelect={() => {}}
                    tags={tags}
                    menuNodeList={menuNodeList}
                    setMenuNodeList={setMenuNodeList}
                    menuId={selectedMenuId}
                    categoryId={currentDirectoryInfo.id}
                    productVersion={productVersion}
                    locationId={locationId}
                    handleOpenDirectoryEditModal={handleOpenDirectoryEditModal}
                    handleOpenProductEditModal={handleOpenProductEditModal}
                    handleOpenMappingModal={handleOpenMappingModal}
                    handleOpenProductOrDirectoryModals={
                      handleOpenProductOrDirectoryModals
                    }
                    setIsProductAdd={setIsProductAdd}
                    setError={setError}
                    onDirectoryNavigate={handleDirectoryNavigate}
                    onProductNavigate={handleProductNavigate}
                    isOpenWrapperEditModal={isOpenWrapperEditModal}
                    isRoot={false}
                    isEditDisabled={isEditDisabled}
                  />
                ),
              )}
          </Grid>
        </div>
      )}

      {/* Product Edit View - Show when editing product inline */}
      {isEditingProductInline && editingProductData && (
        <div style={{ padding: "24px" }}>
          {/* Product edit form would go here */}
          <Typography>Editing Product: {editingProductData.name}</Typography>
        </div>
      )}
    </div>
  );
};

export default MenuDetailsView;
